/**
 * Maintenance request detail — `GET maintenance/{id}/`.
 *
 * PERMISSION MODEL INVERTS THE PATTERN USED EVERYWHERE ELSE IN THIS APP.
 * Edit/Delete are NOT manager-gated here: `maintenance/routes.ts` uses plain
 * `IsAuthenticated` on the whole CRUD surface, so whoever the scoped query
 * lets see this row (including the filing tenant) may also edit or delete it.
 * Only `assign`/`complete` are manager-only (`IsManager`, i.e. LANDLORD/AGENT/
 * SUPERAGENT) — see the route file's top comment.
 *
 * The two status quick actions ("Mark in progress" / "Cancel request") ride on
 * that same broad `PATCH maintenance/{id}/` permission, so they sit in the
 * ungated Edit/Delete row rather than behind `canManage`. There is deliberately
 * NO "Mark completed" quick action: the manager-only "Complete request" form
 * below also writes `completion_notes`, and a second, notes-less path to the
 * same COMPLETED status would let the two disagree.
 *
 * The image upload control is shown to everyone who can see this request
 * (the backend's own check for `POST images/` is broader than "manager": it
 * also allows the request's own tenant), not hidden behind a manager check.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Lightbox } from '../../components/Lightbox';
import {
  assignMaintenanceRequest,
  completeMaintenanceRequest,
  deleteMaintenanceRequest,
  getMaintenanceRequest,
  updateMaintenanceRequest,
  uploadMaintenanceImage,
} from '../../api/endpoints/maintenance';
import { createConversation, sendMessage } from '../../api/endpoints/messaging';
import type { RequestStatus } from '../../types/maintenance';

export function MaintenanceDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const canManage = user?.role === 'LANDLORD' || user?.role === 'AGENT' || user?.role === 'SUPERAGENT';

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [agentId, setAgentId] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);

  const query = useQuery({
    queryKey: ['maintenance-request', requestId],
    queryFn: () => getMaintenanceRequest(requestId),
    enabled: Number.isFinite(requestId),
  });

  const remove = useMutation({
    mutationFn: () => deleteMaintenanceRequest(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-requests'] });
      navigate('..');
    },
  });

  /** `PATCH maintenance/{id}/` with nothing but `status` — the quick actions. */
  const changeStatus = useMutation({
    mutationFn: (status: RequestStatus) => updateMaintenanceRequest(requestId, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-request', requestId] });
    },
  });

  /**
   * "Discuss in Messages". The messaging API has no find-existing-conversation
   * lookup and no prefill field on conversation creation, so this ALWAYS opens
   * a brand-new thread and posts the context line as its own first message.
   */
  const discuss = useMutation({
    mutationFn: async (variables: { counterpartId: number; title: string; requestId: number }) => {
      const conversation = await createConversation({ participants: [variables.counterpartId] });
      await sendMessage(conversation.id, {
        text: `Regarding maintenance request "${variables.title}" (#${variables.requestId}):`,
      });
      return conversation;
    },
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // This page mounts at `<role>/maintenance/:id`; messaging at `<role>/messages/:id`.
      navigate(`../../messages/${conversation.id}`);
    },
  });

  const assign = useMutation({
    mutationFn: () => assignMaintenanceRequest(requestId, { agent_id: Number(agentId) }),
    onSuccess: () => {
      setAssignError(null);
      setAgentId('');
      void queryClient.invalidateQueries({ queryKey: ['maintenance-request', requestId] });
    },
    onError: (caught) => setAssignError(parseApiError(caught).message),
  });

  const complete = useMutation({
    mutationFn: () => completeMaintenanceRequest(requestId, { completion_notes: completionNotes }),
    onSuccess: () => {
      setCompleteError(null);
      void queryClient.invalidateQueries({ queryKey: ['maintenance-request', requestId] });
    },
    onError: (caught) => setCompleteError(parseApiError(caught).message),
  });

  function handleAssign(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setAssignError(null);
    assign.mutate();
  }

  function handleComplete(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setCompleteError(null);
    complete.mutate();
  }

  const uploadImage = useMutation({
    mutationFn: (file: File) => uploadMaintenanceImage(requestId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-request', requestId] });
      setSelectedImageFile(null);
      setImageInputKey((key) => key + 1);
    },
  });

  function handleImageUploadSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (selectedImageFile !== null) uploadImage.mutate(selectedImageFile);
  }

  if (!Number.isFinite(requestId)) {
    return <p>Invalid maintenance request id.</p>;
  }

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const request = query.data;
  const viewableImages = request.images
    .filter((image): image is typeof image & { image: string } => image.image !== null)
    .map((image) => ({
      src: image.image,
      alt: image.caption === '' ? `Maintenance photo ${image.id}` : image.caption,
    }));

  /**
   * A manager messages the filing tenant; a tenant messages the assigned agent.
   * `assigned_agent` is null until someone is assigned, and this model carries
   * no landlord id to fall back to — so the action is hidden, not redirected.
   */
  const counterpartId = canManage ? request.tenant : request.assigned_agent;
  const isTerminal = request.status === 'COMPLETED' || request.status === 'CANCELLED';

  return (
    <section>
      <h2>{request.title}</h2>

      <p>
        <Link to="edit">Edit</Link>{' '}
        <button type="button" className="button--danger" onClick={() => setConfirmDeleteOpen(true)}>
          Delete
        </button>{' '}
        {!isTerminal && request.status !== 'IN_PROGRESS' && (
          <button
            type="button"
            disabled={changeStatus.isPending}
            onClick={() => changeStatus.mutate('IN_PROGRESS')}
          >
            Mark in progress
          </button>
        )}{' '}
        {!isTerminal && (
          <button
            type="button"
            disabled={changeStatus.isPending}
            onClick={() => changeStatus.mutate('CANCELLED')}
          >
            Cancel request
          </button>
        )}{' '}
        {counterpartId !== null && (
          <button
            type="button"
            disabled={discuss.isPending}
            onClick={() =>
              discuss.mutate({ counterpartId, title: request.title, requestId: request.id })
            }
          >
            {discuss.isPending ? 'Opening…' : 'Discuss in Messages'}
          </button>
        )}
      </p>

      {changeStatus.isError && (
        <p role="alert" className="form__error">
          {parseApiError(changeStatus.error).message}
        </p>
      )}

      {discuss.isError && (
        <p role="alert" className="form__error">
          {parseApiError(discuss.error).message}
        </p>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete maintenance request"
        message={`Delete request "${request.title}"? This cannot be undone.`}
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {remove.isError && (
        <p role="alert" className="form__error">
          {parseApiError(remove.error).message}
        </p>
      )}

      <dl>
        <dt>Status</dt>
        <dd>{request.status}</dd>
        <dt>Priority</dt>
        <dd>{request.priority}</dd>
        <dt>Category</dt>
        <dd>{request.category}</dd>
        <dt>Tenant</dt>
        <dd>{request.tenant_name}</dd>
        <dt>Unit</dt>
        <dd>{request.unit_number}</dd>
        <dt>Property</dt>
        <dd>{request.property_name}</dd>
        <dt>Assigned agent</dt>
        <dd>{request.agent_name ?? '—'}</dd>
        <dt>Description</dt>
        <dd>{request.description}</dd>
        <dt>Completion notes</dt>
        <dd>{request.completion_notes === '' ? '—' : request.completion_notes}</dd>
        <dt>Filed</dt>
        <dd>{request.created_at}</dd>
        <dt>Completed</dt>
        <dd>{request.completed_at ?? '—'}</dd>
      </dl>

      <h3>Images ({request.images.length})</h3>
      {request.images.length === 0 && <p>No images uploaded.</p>}
      {request.images.length > 0 && (
        <ul className="gallery">
          {request.images.map((image) => (
            <li key={image.id} className="gallery__item">
              {image.image !== null ? (
                <button
                  type="button"
                  className="gallery__trigger"
                  onClick={() =>
                    setLightboxIndex(viewableImages.findIndex((viewable) => viewable.src === image.image))
                  }
                >
                  <img
                    src={image.image}
                    alt={image.caption === '' ? `Maintenance photo ${image.id}` : image.caption}
                    loading="lazy"
                  />
                </button>
              ) : (
                <span className="thumb thumb--placeholder" aria-hidden="true">
                  —
                </span>
              )}
              {image.caption !== '' && <span className="gallery__caption">{image.caption}</span>}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleImageUploadSubmit} noValidate>
        {uploadImage.isError && (
          <p role="alert" className="form__error">
            {parseApiError(uploadImage.error).message}
          </p>
        )}
        <label htmlFor="maintenance-image-upload">Upload image</label>{' '}
        <input
          key={imageInputKey}
          id="maintenance-image-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(event) => setSelectedImageFile(event.target.files?.[0] ?? null)}
        />{' '}
        <button type="submit" disabled={selectedImageFile === null || uploadImage.isPending}>
          {uploadImage.isPending ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      {canManage && (
        <>
          <h3>Assign agent</h3>
          <form onSubmit={handleAssign} noValidate>
            {assignError !== null && (
              <p role="alert" className="form__error">
                {assignError}
              </p>
            )}
            <label htmlFor="assign-agent-id">Agent id</label>
            <input
              id="assign-agent-id"
              type="number"
              required
              value={agentId}
              onChange={(event) => setAgentId(event.target.value)}
            />
            <button type="submit" disabled={assign.isPending}>
              {assign.isPending ? 'Assigning…' : 'Assign'}
            </button>
          </form>

          <h3>Complete request</h3>
          <form onSubmit={handleComplete} noValidate>
            {completeError !== null && (
              <p role="alert" className="form__error">
                {completeError}
              </p>
            )}
            <label htmlFor="completion-notes">Completion notes</label>
            <textarea
              id="completion-notes"
              value={completionNotes}
              onChange={(event) => setCompletionNotes(event.target.value)}
            />
            <button type="submit" disabled={complete.isPending}>
              {complete.isPending ? 'Completing…' : 'Mark completed'}
            </button>
          </form>
        </>
      )}

      <Lightbox
        images={viewableImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
}

export default MaintenanceDetailPage;
